import { APP_CONTEXTS } from "app/model"
import { getStore } from "app/store/store"
import { ALL_AUCTION_TYPES, AUCTION_TYPES, getDefaultAuctionTypes, IAuction } from "auction/model"
import { auctionRPO } from "auction/repository"
import { IForm, IFormDataLoader } from "form/model"
import { formActionCreator } from "form/store/actions"

export function addAuctionsToNewCampaign(
  tempCampaignId: number,
  isForecast: boolean,
  isDspDeal: boolean
): Promise<void> {
  return new Promise<void>(resolve => {
    const toAddAuctionTypes: AUCTION_TYPES[] = getDefaultAuctionTypes(isDspDeal)
    if (toAddAuctionTypes.length === 0) {
      // auction type is set on the dsp_deal object
      return resolve()
    }
    const auctionAssociations: IAuction[] = []
    const toProcessPromiseCount = toAddAuctionTypes.length
    let proccessedPromise = 0
    const onFinish = (auctionAssociation: IAuction | null) => {
      if (auctionAssociation) {
        auctionAssociations.push(auctionAssociation)
      }
      if (toProcessPromiseCount === ++proccessedPromise) {
        getStore().dispatch(
          formActionCreator.setFormAuctions(
            isForecast ? APP_CONTEXTS.FORECAST_CREATE : APP_CONTEXTS.CAMPAIGN_CREATE,
            auctionAssociations,
            false
          )
        )
        resolve()
      }
    }
    for (const auctiontype of toAddAuctionTypes) {
      auctionRPO
        .addAuction(tempCampaignId, auctiontype)
        .then(auctionAssociation => onFinish(auctionAssociation))
        .catch(error => {
          console.error(error)
          onFinish(null)
        })
    }
  })
}

export async function synchronizeAuctions(form: IForm): Promise<void> {
  const { tempCampaignId: campaignId } = form
  const { auctions: existingAuctions } = form.context.relations
  const { auctions: toSetAuctionTypes } = form.configuration.budget
  if (typeof campaignId !== "number") {
    return Promise.reject("campaignId must be a number")
  }
  if (campaignId < 0) {
    return Promise.reject("campaignId must be equals or greater then 0")
  }
  if (!Array.isArray(toSetAuctionTypes)) {
    return Promise.reject("newAuctions must be an array")
  }
  for (const toSetAuctionType of toSetAuctionTypes) {
    if (!ALL_AUCTION_TYPES.includes(toSetAuctionType)) {
      return Promise.reject(
        `Invalid value in toSetAuctionTypes. Provided ${toSetAuctionType}. Available: ${ALL_AUCTION_TYPES.join(",")}`
      )
    }
  }
  if (Array.isArray(existingAuctions)) {
    for (const existingAuction of existingAuctions) {
      if (typeof existingAuction !== "object") {
        return Promise.reject("An existing auction is invalid")
      }
    }
  }
  let hasError: boolean = false
  let serverSideAuctions: IAuction[] = []
  for (const toSetAuctionType of toSetAuctionTypes) {
    const existingAuction = !existingAuctions
      ? undefined
      : existingAuctions.find(a => a.auction_type_id === toSetAuctionType)
    if (!existingAuction) {
      try {
        serverSideAuctions.push(await auctionRPO.addAuction(campaignId, toSetAuctionType))
      } catch (error) {
        hasError = true
        console.error("Unable to add auction", error)
      }
    } else {
      serverSideAuctions.push(existingAuction)
    }
  }
  if (existingAuctions) {
    for (const existingAuction of existingAuctions) {
      if (!toSetAuctionTypes.includes(existingAuction.auction_type_id)) {
        try {
          await auctionRPO.deleteAuction(campaignId, existingAuction)
        } catch (error) {
          hasError = true
          console.error("Unable to delete auction", error)
          serverSideAuctions.push(existingAuction)
        }
      }
    }
  }
  if (hasError) {
    try {
      serverSideAuctions = await auctionRPO.getAuctions(form.tempCampaignId)
    } catch (error) {
      // it doesn't smell good here ...
      console.error("Cannot fallback ...", error)
      return
    }
  }
  getStore().dispatch(
    formActionCreator.setRelations(form.context.appContext, { auctions: serverSideAuctions, auctionsError: hasError })
  )
}

export const auctionFormDataLoader: IFormDataLoader = async (form: IForm, onFinish: (error?: string) => any) => {
  try {
    const auctions = await auctionRPO.getAuctions(form.tempCampaignId)
    form.context.relations.auctions = auctions.length === 0 ? null : auctions
    form.configuration.budget.auctions = auctions.map(a => a.auction_type_id)
    onFinish()
  } catch (error) {
    console.error(error)
    onFinish(`Auction data-loader error ${String(error)}`)
  }
}
